import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { FormattingTextarea } from 'react-telegram-input-text-entities';
import { v4 as uuidv4 } from "uuid";
import StaticLayoutInner from '@/layouts/StaticLayoutInner';
import { Input } from '@/components/Form/Input';
import { GenericForm } from '@/components/Form/GenericForm';
import { useFormHandler } from '@/hooks/useFormHandler';
import { OutletContext, Project } from '@/types';
import { fetchProject, updateProject } from '@/actions';
import { fetchFormDataAPI } from '@/api';
import EditProjectHeader from '@/pages/settings/EditProject/EditProjectHeader';
import Plus from '@/images/icons/Plus';
import Edit from '@/images/icons/Edit';
import PrivacyModal from '@/pages/settings/EditProject/PrivacyModal';
import TermsModal from '@/pages/settings/EditProject/TermsModal';
import { useWindowWidth } from '@/hooks/useWindowWidth';

function EditProject() {
	const { auth, project } = useOutletContext<OutletContext>();
	const dispatch = useDispatch();
    const windowWidth = useWindowWidth();

	const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
	const [termsModalOpen, setTermsModalOpen] = useState(false);
	const [linkPrivacyLocal, setLinkPrivacyLocal] = useState<string | null>(null);
	const [linkTermsLocal, setLinkTermsLocal] = useState<string | null>(null);
	const [descriptionMessageKey, setDescriptionMessageKey] = useState<string>(uuidv4());

	useEffect(() => {
		dispatch(fetchProject(auth.user.token, project.projectId));
	}, []);

	const submitCallback = async (updatedProject: Partial<Project>) => {
		let formData = new FormData();

		formData.append('projectId', project.projectId);

		if (updatedProject.name) {
			formData.append('projectName', updatedProject.name);
		}
		if (updatedProject.descriptionMessage?.text) {
			formData.append('projectDescription', updatedProject.descriptionMessage?.text || '');
		}
		if (updatedProject.descriptionMessage?.entities) {
			formData.append('projectDescriptionEntities', JSON.stringify(updatedProject.descriptionMessage?.entities));
		}
		if (updatedProject.linkPrivacy) {
			formData.append('linkPrivacy', updatedProject.linkPrivacy || '');
		}
		if (updatedProject.linkTerms) {
			formData.append('linkTerms', updatedProject.linkTerms || '');
		}

		try {
			const response = await fetchFormDataAPI("/project/update", auth.user.token, formData);
			const result = await response.json();

			if (response.ok && result.project) {
				dispatch(updateProject(result.project));
			} else {
				throw new Error(result.message || "Failed to update the project.");
			}
		} catch (error: any) {
			console.error("Error updating project:", error.message);
			throw error;
		}
	};

	const {
		formData,
		initialState,
		formReset,
		isLoading,
		isSuccess,
		error,
		formRef,
		onFieldChange,
		onSave,
	} = useFormHandler<Project>(project, submitCallback);

	useEffect(() => {
		setLinkPrivacyLocal(formData.linkPrivacy || null);
		setLinkTermsLocal(formData.linkTerms || null);
	}, []);

	useEffect(() => {
		if (isSuccess) {
			setPrivacyModalOpen(false);
			setTermsModalOpen(false);
		}
	}, [isSuccess]);

	const onUndo = () => {
		formReset();
		setDescriptionMessageKey(uuidv4());
	};

	return (
		<StaticLayoutInner centerContent>
			<div className="flex flex-col min-h-full w-full">
				<EditProjectHeader project={project} />
				<GenericForm
					formData={formData}
					initialState={initialState}
					onFieldChange={onFieldChange}
					onSave={onSave}
					onUndo={() => onUndo()}
					formRef={formRef}
					isLoading={isLoading}
					className="w-full max-w-3xl mb-6"
                    buttonsVertical={windowWidth < 640}
				>
					<Input
						className="h-11 mb-6 md:mb-4"
						type="text"
						name="projectName"
						value={formData.name}
						onChange={(e) => onFieldChange('name', e.target.value)}
						required
					>
						Name
					</Input>
					<div className="-mb-4 md:mb-4">
						<FormattingTextarea
							key={descriptionMessageKey}
							value={formData.descriptionMessage?.text}
							disabled={false}
							onChange={(text: string, entities: []) => { onFieldChange('descriptionMessage', { ...formData.descriptionMessage, text, entities });  }}
							className="h-[300px] md:!h-[480px] overflow-y-auto !rounded-[4px] md:!rounded-[6px] bg-transparent border-[#dde1dd] border-t-0 focus:!border-b-link-blue focus:!border-l-link-blue focus:!border-r-link-blue"
							clearClassName="text-link-blue"
							entities={formData.descriptionMessage?.entities}
							hasPro={true}
							isTrialSubscription={false}
							messageInput={true}
							label="Description"
							labelClassName="flex w-full h-full select-none pointer-events-none absolute left-0 font-normal truncate peer-placeholder-shown:text-blue-gray-500 leading-[1.2] md:leading-none peer-focus:leading-[1.2] md:peer-focus:leading-none peer-disabled:text-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500 transition-all -top-1.5 peer-placeholder-shown:text-base text-[12px] md:text-[13px] peer-focus:text-[12px] md:peer-focus:text-[13px] before:content[' '] before:block before:box-border before:w-2 md:before:w-[13px] before:h-1.5 before:mt-[5px] before:mr-1 peer-placeholder-shown:before:border-transparent before:rounded-tl-[4px] md:before:rounded-tl-[6px] md:before:translate-y-px before:border-t before:border-l before:pointer-events-none before:transition-all peer-disabled:before:border-transparent after:content[' '] after:block after:flex-grow after:box-border after:w-2.5 after:h-1.5 after:mt-[5px] after:ml-1 peer-placeholder-shown:after:border-transparent after:rounded-tr-[4px] md:after:rounded-tr-[6px] md:after:translate-y-px after:border-t after:border-r after:pointer-events-none after:transition-all peer-disabled:after:border-transparent peer-placeholder-shown:leading-[3.5] text-brand-green/[.5] peer-focus:text-link-blue before:border-[#dde1dd] peer-focus:before:!border-link-blue after:border-[#dde1dd] peer-focus:after:!border-link-blue"
							buttonActiveClassName="bg-[#F7F6F2]"
							buttonClassName="transition rounded-md hover:bg-[#F7F6F2]"
							menuMobileBottom={0}
							menuMobileBorder="border-t-[0.5px] border-b-[0.5px] border-t-[#F2F2F2] border-b-[#F2F2F2]"
							menuMobileShadow="shadow-[-2px_-1px_6px_0px_#07366105]"
							menuShadow="shadow-[0px_0px_8px_0px_#C8C8C81A]"
						/>
					</div>
					{(error || isSuccess) && (
						<div className="mt-3">
							{error && <div className="text-sm mt-2 font-medium text-red-orange">{error}</div>}
							{isSuccess && <div className="text-sm mt-2 font-medium text-green-500">Project updated successfully!</div>}
						</div>
					)}
				</GenericForm>
				<div className="flex gap-3 flex-col sm:flex-row mt-auto">
					<button className="btn-secondary-inverted bg-transparent" onClick={() => setPrivacyModalOpen(true)}>
						{linkPrivacyLocal ? (
							<>
								Edit Privacy Policy
								<Edit className="w-5 h-5 ml-1 -mr-1" />
							</>
						) : (
							<>
								Add Privacy Policy
								<Plus className="w-5 h-5 ml-1 -mr-1" />
							</>
						)}
					</button>
					<button className="btn-secondary-inverted bg-transparent" onClick={() => setTermsModalOpen(true)}>
						{linkTermsLocal ? (
							<>
								Edit Terms of Service
								<Edit className="w-5 h-5 ml-1 -mr-1" />
							</>
						) : (
							<>
								Add Terms of Service
								<Plus className="w-5 h-5 ml-1 -mr-1" />
							</>
						)}
					</button>
				</div>
				<PrivacyModal
					modalOpen={privacyModalOpen}
					setModalOpen={setPrivacyModalOpen}
					modalError={error}
					privacyData={formData.linkPrivacy}
					submitCallback={onSave}
					isLoading={isLoading}
					onFieldChange={onFieldChange}
				/>
				<TermsModal
					modalOpen={termsModalOpen}
					setModalOpen={setTermsModalOpen}
					modalError={error}
					termsData={formData.linkTerms}
					submitCallback={onSave}
					isLoading={isLoading}
					onFieldChange={onFieldChange}
				/>
			</div>
		</StaticLayoutInner>
	);
}

export default EditProject;
